import { useMediaQuery } from "@mui/material";
import { MOBILE_BREAKPOINT } from "utils/constants";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  TextInput,
  TopToolbar,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { AcademicRequestStatus } from "utils/choices";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { EmptyList } from "server/components";
import { settings } from "app";
import ChangeStatusDialog from "../components/ChangeStatusDialog";
import { AcademicRequestDataIssueExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { accountRequests } from "api/accountApi";
import { MobileListRow } from "features/MobileListRow";

const NOTE_PREVIEW_LENGTH = 80;

const truncateNote = note => {
  if (!note) return "-";
  return note.length > NOTE_PREVIEW_LENGTH ? `${note.slice(0, NOTE_PREVIEW_LENGTH)}…` : note;
};

const renderFullName = rec =>
  rec.secondName ? `${rec.firstName} ${rec.secondName} ${rec.lastName}` : `${rec.firstName} ${rec.lastName}`;

const listFilters = [
  <TextInput
    source="facultyNumber"
    label="Факултетен номер"
    alwaysOn
  />,
  <ReferenceInput
    source="topicId"
    reference={studentRequests.getAcademicRequestDataIssueTopicsSelect()}
    alwaysOn
  >
    <SelectInput
      label="Тема"
      optionText="name"
      emptyText="Всички теми"
    />
  </ReferenceInput>,
  <SelectInput
    source="status"
    label="Статус"
    choices={AcademicRequestStatus}
    alwaysOn
    emptyText="Всички"
  />,
];

const ListActions = () => (
  <TopToolbar>
    <ExportXlsButton exporter={AcademicRequestDataIssueExporter} />
  </TopToolbar>
);

const ListAcademicRequestDataIssues = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <List
      title="Проблем с прехвърлените данни"
      actions={<ListActions />}
      filters={listFilters}
      exporter={false}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Факултетен номер", value: record.facultyNumber }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Студент",
                    value: (
                      <ReferenceField
                        reference={accountRequests.getUsersSelect()}
                        source="userId"
                      >
                        <FunctionField render={renderFullName} />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
            </div>
          )}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[
                  {
                    label: "Специалност",
                    value: (
                      <ReferenceField
                        source="specialityId"
                        reference={administrationRequests.getSpecialitiesSelect()}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Тема", value: record.topicNameBg }]} />
              <MobileListRow fields={[{ label: "Забележка", value: truncateNote(record.note) }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Създаден",
                    value: (
                      <DateField
                        source="createdOn"
                        locales="bg-BG"
                        showTime
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value: (
                      <SelectField
                        source="status"
                        choices={AcademicRequestStatus}
                      />
                    ),
                  },
                ]}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                <SecuredComponent permission={permissions.canChangeAcademicRequestStatus}>
                  <ChangeStatusDialog record={record} />
                </SecuredComponent>
              </div>
            </div>
          )}
          tertiaryText={() => (
            <SecuredComponent permission={permissions.canPreviewAcademicRequestDataIssues}>
              <ShowButton label="" />
            </SecuredComponent>
          )}
        ></SimpleList>
      ) : (
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
        >
          <ReferenceField
            reference={accountRequests.getUsersSelect()}
            source="userId"
            label="Студент"
            sortable={false}
          >
            <FunctionField render={renderFullName} />
          </ReferenceField>
          <TextField
            source="facultyNumber"
            label="Факултетен номер"
            sortable={false}
          />
          <ReferenceField
            source="specialityId"
            label="Специалност"
            reference={administrationRequests.getSpecialitiesSelect()}
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField
            source="topicNameBg"
            label="Тема"
            sortable={false}
          />
          <FunctionField
            label="Забележка"
            sortable={false}
            render={record => truncateNote(record.note)}
          />
          <DateField
            source="createdOn"
            label="Създаден"
            locales="bg-BG"
            showTime
          />
          <SelectField
            source="status"
            label="Статус"
            choices={AcademicRequestStatus}
          />
          <SecuredComponent permission={permissions.canChangeAcademicRequestStatus}>
            <FunctionField
              label="Статус"
              render={record => <ChangeStatusDialog record={record} />}
            />
          </SecuredComponent>
          <SecuredComponent permission={permissions.canPreviewAcademicRequestDataIssues}>
            <ShowButton />
          </SecuredComponent>
        </Datagrid>
      )}
    </List>
  );
};

export default ListAcademicRequestDataIssues;
